import React, { useState, useEffect } from 'react';

// Simple inline stubs for UI components
const Icon = ({ name, size = 16, className = '' }) => {
  const sizeStr = size + 'px';
  return <span className={className} style={{ display: 'inline-flex', width: sizeStr, height: sizeStr }} />;
};
const Button = ({ children, onClick, variant, disabled, className = '', type, loading, ...rest }) => (
  <button type={type || 'button'} onClick={onClick} disabled={disabled || loading} className={`px-4 py-2 rounded-lg font-medium transition-colors ${variant === 'outline' ? 'border border-border bg-card text-gray-700 hover:bg-gray-50' : 'bg-blue-600 text-white hover:bg-blue-700'} ${disabled ? 'opacity-50 cursor-not-allowed' : ''} ${className}`} {...rest}>{loading ? 'Loading...' : children}</button>
);
const Input = ({ className = '', label, error, required, ...props }) => (
  <div>
    {label && <label className="block text-sm font-medium text-gray-700 mb-1">{label}{required && ' *'}</label>}
    <input className={`w-full border border-border rounded-lg px-3 py-2 text-sm focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary focus-visible:outline-none ${error ? 'border-red-500' : ''} ${className}`} {...props} />
    {error && <p className="text-xs text-red-600 mt-1">{error}</p>}
  </div>
);
const Select = ({ value, onChange, options, placeholder, className = '', label, error, required, disabled }) => (
  <div>
    {label && <label className="block text-sm font-medium text-gray-700 mb-1">{label}{required && ' *'}</label>}
    <select value={value} onChange={e => onChange(e.target.value)} disabled={disabled} className={`w-full border border-border rounded-lg px-3 py-2 text-sm focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary focus-visible:outline-none bg-card ${error ? 'border-red-500' : ''} ${className}`}>
      {placeholder && <option value="">{placeholder}</option>}
      {options?.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
    </select>
    {error && <p className="text-xs text-red-600 mt-1">{error}</p>}
  </div>
);

const EditProjectModal = ({ isOpen, onClose, project }) => {
  const [formData, setFormData] = useState({
    projectId: '',
    name: '',
    budget: '',
    startDate: '',
    endDate: '',
    teamMembers: '',
    projectOwner: '',
    description: '',
    status: 'Upcoming'
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Mock employee list for Project Owner dropdown
  const employees = [
    { value: 'emp-001', label: 'Sarah Johnson - Project Manager' },
    { value: 'emp-002', label: 'Michael Chen - Senior Developer' },
    { value: 'emp-003', label: 'Emily Rodriguez - Team Lead' },
    { value: 'emp-004', label: 'David Kim - Product Owner' },
    { value: 'emp-005', label: 'Jessica Williams - Business Analyst' },
    { value: 'emp-006', label: 'Robert Taylor - Technical Lead' },
    { value: 'emp-007', label: 'Amanda Martinez - Scrum Master' },
    { value: 'emp-008', label: 'James Anderson - Engineering Manager' }
  ];

  useEffect(() => {
    if (project) {
      setFormData({
        projectId: project?.projectId || '',
        name: project?.name || '',
        budget: project?.budget || '',
        startDate: project?.dateRange?.start || '',
        endDate: project?.dateRange?.end || '',
        teamMembers: project?.teamMembers?.toString() || '',
        projectOwner: project?.projectOwner || '',
        description: project?.description || '',
        status: project?.status || 'Upcoming'
      });
    }
  }, [project]);

  const handleInputChange = (e) => {
    const { name, value } = e?.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors?.[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleSelectChange = (name, value) => {
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData?.projectId?.trim()) {
      newErrors.projectId = 'Project ID is required';
    }

    if (!formData?.name?.trim()) {
      newErrors.name = 'Project name is required';
    }

    if (!formData?.budget?.trim()) {
      newErrors.budget = 'Budget is required';
    }

    if (!formData?.startDate) {
      newErrors.startDate = 'Start date is required';
    }

    if (!formData?.endDate) {
      newErrors.endDate = 'End date is required';
    }

    if (formData?.startDate && formData?.endDate && formData?.startDate > formData?.endDate) {
      newErrors.endDate = 'End date must be after start date';
    }

    if (!formData?.teamMembers || isNaN(parseInt(formData?.teamMembers)) || parseInt(formData?.teamMembers) < 1) {
      newErrors.teamMembers = 'At least 1 team member required';
    }

    if (!formData?.projectOwner) {
      newErrors.projectOwner = 'Project owner is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      await new Promise(resolve => setTimeout(resolve, 1500));
      console.log('Updated project:', { ...project, ...formData });

      onClose();
    } catch (error) {
      console.error('Error updating project:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-card border-b border-border px-6 py-4">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-semibold text-gray-900 flex items-center">
              <Icon name="Edit" size={24} className="mr-2 text-primary" />
              Edit Project
            </h2>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              disabled={isSubmitting}
            >
              <Icon name="X" size={20} />
            </button>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Project Details */}
          <div className="space-y-4">
            <h3 className="text-lg font-medium text-gray-900">Project Details</h3>

            <Input
              label="Project ID"
              name="projectId"
              value={formData?.projectId}
              onChange={handleInputChange}
              error={errors?.projectId}
              required
              placeholder="e.g., PRJ-2026-001"
              disabled={isSubmitting}
            />

            <Input
              label="Project Name"
              name="name"
              value={formData?.name}
              onChange={handleInputChange}
              error={errors?.name}
              required
              placeholder="e.g., HR System Modernization"
              disabled={isSubmitting}
            />

            <Select
              label="Project Owner"
              value={formData?.projectOwner}
              onChange={(value) => handleSelectChange('projectOwner', value)}
              options={[
                { value: '', label: 'Select project owner...' },
                ...employees
              ]}
              error={errors?.projectOwner}
              required
              disabled={isSubmitting}
            />

            <Input
              label="Budget"
              name="budget"
              value={formData?.budget}
              onChange={handleInputChange}
              error={errors?.budget}
              required
              placeholder="e.g., $125,000"
              disabled={isSubmitting}
            />

            <div className="grid grid-cols-2 gap-4">
              <Input
                label="Start Date"
                type="date"
                name="startDate"
                value={formData?.startDate}
                onChange={handleInputChange}
                error={errors?.startDate}
                required
                disabled={isSubmitting}
              />

              <Input
                label="End Date"
                type="date"
                name="endDate"
                value={formData?.endDate}
                onChange={handleInputChange}
                error={errors?.endDate}
                required
                disabled={isSubmitting}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <Input
                label="Number of Team Members"
                type="number"
                name="teamMembers"
                value={formData?.teamMembers}
                onChange={handleInputChange}
                error={errors?.teamMembers}
                required
                min="1"
                placeholder="e.g., 8"
                disabled={isSubmitting}
              />

              <Select
                label="Status"
                value={formData?.status}
                onChange={(value) => handleSelectChange('status', value)}
                options={[
                  { value: 'Upcoming', label: 'Upcoming' },
                  { value: 'Active', label: 'Active' },
                  { value: 'Completed', label: 'Completed' },
                  { value: 'On Hold', label: 'On Hold' }
                ]}
                disabled={isSubmitting}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Description
              </label>
              <textarea
                name="description"
                value={formData?.description}
                onChange={handleInputChange}
                rows={4}
                className="w-full px-3 py-2 border border-border rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary"
                placeholder="Provide a brief description of the project..."
                disabled={isSubmitting}
              />
            </div>
          </div>

          {/* Actions */}
          <div className="flex justify-end space-x-3 pt-4 border-t border-border">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              loading={isSubmitting}
            >
              Update Project
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditProjectModal;
